import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import Header from "../components/Header";

export default function EditBook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);

  // Editable fields — pre-filled from Firestore once book loads
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [publisher, setPublisher] = useState("");
  const [year, setYear] = useState("");
  const [totalPages, setTotalPages] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "books", id), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setBook({ id: snap.id, ...data });
        // Only pre-fill on first load
        setTitle(data.title || "");
        setAuthor(data.author || "");
        setPublisher(data.publisher || "");
        setYear(data.year || "");
        setTotalPages(String(data.totalPages || ""));
        setCoverUrl(data.coverUrl || "");
      }
    });
    return unsubscribe;
  }, [id]);

  async function handleSave() {
    if (!title.trim()) return alert("Title is required.");
    if (!totalPages || parseInt(totalPages) < 1) return alert("Please enter a valid page count.");

    // Warn if new page count is lower than pages already read
    if (book && parseInt(totalPages) < (book.pagesRead || 0)) {
      return alert(`Page count cannot be less than pages already read (${book.pagesRead}).`);
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, "books", id), {
        title: title.trim(),
        author: author.trim() || "Unknown",
        publisher: publisher.trim(),
        year: year.trim(),
        totalPages: parseInt(totalPages),
        coverUrl: coverUrl.trim() || null,
      });
      navigate(`/book/${id}`);
    } catch {
      alert("Failed to save changes. Try again.");
    }
    setSaving(false);
  }

  if (!book) return <div style={styles.loading}>Loading...</div>;

  return (
    <div style={styles.container}>
      <Header />

      <div style={styles.pageHeader}>
        <button style={styles.backBtn} onClick={() => navigate(`/book/${id}`)}>← Back</button>
        <div style={styles.headerText}>
          <h2 style={styles.title}>Edit Book Details</h2>
          <p style={styles.subtitle}>{book.title}</p>
        </div>
      </div>

      <div style={styles.content}>

        {/* Cover preview */}
        {coverUrl ? (
          <div style={styles.coverPreviewRow}>
            <img src={coverUrl} alt="Cover preview" style={styles.coverPreview} />
            <p style={styles.coverPreviewLabel}>Cover preview</p>
          </div>
        ) : (
          <div style={styles.noCoverPreview}>
            <p style={styles.noCoverText}>📖 No cover image</p>
          </div>
        )}

        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Book Information</h3>

          <label style={styles.label}>Title *</label>
          <input
            style={styles.input}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <label style={styles.label}>Author</label>
          <input
            style={styles.input}
            type="text"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
          />

          <label style={styles.label}>Publisher</label>
          <input
            style={styles.input}
            type="text"
            value={publisher}
            onChange={(e) => setPublisher(e.target.value)}
          />

          <label style={styles.label}>Year Published</label>
          <input
            style={styles.input}
            type="text"
            placeholder="e.g. 2019"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          />
        </div>

        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Page Count</h3>
          <p style={styles.pageCountNote}>
            Currently {book.pagesRead || 0} of {book.totalPages} pages read.
            New total must be at least {book.pagesRead || 0}.
          </p>
          <label style={styles.label}>Total Pages *</label>
          <input
            style={styles.input}
            type="number"
            value={totalPages}
            onChange={(e) => setTotalPages(e.target.value)}
          />
        </div>

        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Cover Image</h3>
          <p style={styles.coverNote}>
            Paste a direct image URL to update the cover, or leave blank to remove it.
          </p>
          <label style={styles.label}>Cover URL</label>
          <input
            style={styles.input}
            type="text"
            placeholder="https://..."
            value={coverUrl}
            onChange={(e) => setCoverUrl(e.target.value)}
          />
        </div>

        <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "💾 Save Changes"}
        </button>

        <button style={styles.cancelBtn} onClick={() => navigate(`/book/${id}`)}>
          Cancel
        </button>

      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" },

  pageHeader: { backgroundColor: "white", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" },
  backBtn: { padding: "8px 14px", backgroundColor: "#e2e8f0", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, whiteSpace: "nowrap" },
  headerText: { flex: 1 },
  title: { fontSize: 18, color: "#2d3748", margin: "0 0 2px 0" },
  subtitle: { fontSize: 13, color: "#718096", margin: 0 },

  content: { padding: 24, maxWidth: 600, margin: "0 auto" },

  coverPreviewRow: { display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 20 },
  coverPreview: { width: 90, height: 135, objectFit: "cover", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
  coverPreviewLabel: { fontSize: 12, color: "#a0aec0", marginTop: 8 },
  noCoverPreview: { textAlign: "center", padding: "16px 0 8px 0" },
  noCoverText: { fontSize: 14, color: "#a0aec0" },

  card: { backgroundColor: "white", borderRadius: 12, padding: 24, marginBottom: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" },
  sectionTitle: { fontSize: 16, color: "#2d3748", margin: "0 0 16px 0" },
  label: { display: "block", fontWeight: "bold", color: "#4a5568", marginBottom: 6, fontSize: 14 },
  input: { width: "100%", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 15, boxSizing: "border-box", marginBottom: 16 },

  pageCountNote: { fontSize: 13, color: "#718096", margin: "0 0 12px 0", padding: "8px 12px", backgroundColor: "#f7fafc", borderRadius: 8 },
  coverNote: { fontSize: 13, color: "#718096", margin: "0 0 12px 0" },

  saveBtn: { width: "100%", padding: 16, backgroundColor: "#48bb78", color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer", fontWeight: "bold", marginBottom: 12 },
  cancelBtn: { width: "100%", padding: 14, backgroundColor: "#e2e8f0", color: "#4a5568", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer" },
};